import { useCallback, useContext, useEffect, useState } from 'react';

import { type Blueprint } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { BlueprintOverheadDimensionsToggle } from '../shared/blueprint-overhead-dimensions-toggle';
import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { DisplayMode } from '@r-core/controller/enums/display-mode.enum';
import { EmbeddedSvgViewport } from '../shared/embedded-svg-viewport';
import { PAGE_BODY_PADDING } from '../shared/blueprint-view-styles';
import type { BlueprintPage } from '../types';

export type BlueprintOverheadLayoutPageProps = {
	page: BlueprintPage;
	blueprint: Blueprint;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

export const BlueprintOverheadLayoutPage = (props: BlueprintOverheadLayoutPageProps): React.ReactElement => {
	const {
		page,
		blueprint,
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl
	} = props;

	// Use the default island for the overhead view
	const kitchen = blueprint.kitchens.getActiveVersion();
	const defaultIsland = kitchen?.design.islands.getDefaultIsland();
	const defaultIslandId = defaultIsland?.id ?? '';

	// Only the Overhead Layout sheet carries the toggle -- Base Layout already forces these dims off
	// unconditionally (embedded-svg-viewport.tsx), and Countertop Layout was never the reported symptom.
	const isOverheadLayout = page.pageType === 'overhead-layout';
	const scope = page.overrideScope ?? `page.${page.pageNumber}`;
	const overrideKey = `${scope}.hideOverheadDimensions`;

	// Local state drives the UI (kitchen.blueprintOverrides is write-through only) -- same pattern as BlueprintPageFontControl, since blueprint-view never subscribes to ('kitchen','updated').
	const mainContext = useContext(AppContext);
	const overridesKitchen = mainContext?.controller?.kitchen ?? kitchen ?? null;
	const [hideOverheadDimensions, setHideOverheadDimensions] = useState<boolean>(
		() => overridesKitchen?.blueprintOverrides?.[overrideKey] === '1'
	);

	useEffect(() => {
		setHideOverheadDimensions(overridesKitchen?.blueprintOverrides?.[overrideKey] === '1');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [overridesKitchen, overrideKey]);

	const toggleHideOverheadDimensions = useCallback((): void => {
		const next = !hideOverheadDimensions;
		setHideOverheadDimensions(next);
		overridesKitchen?.setBlueprintOverride(overrideKey, next ? '1' : '0');
	}, [hideOverheadDimensions, overridesKitchen, overrideKey]);

	return (
		<>
			<BlueprintPageWrapper pageNumber={page.pageNumber}>
				<BlueprintPageHeader
					title={page.title}
					pageNumber={page.pageNumber}
					customerName={customerName}
					layoutDescription={layoutDescription}
					designerName={designerName}
					creationDate={creationDate}
					threeDImageUrl={threeDImageUrl}
					showThumbnail={true}
				/>

				<div style={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: PAGE_BODY_PADDING,
					overflow: 'hidden'
				}}>
					<EmbeddedSvgViewport
						blueprint={blueprint}
						islandId={defaultIslandId}
						panelId={null}
						displayMode={DisplayMode.OverheadManufacturing}
						pageIndex={page.pageNumber}
						pageType={page.pageType}
						hideOverheadDimensions={isOverheadLayout && hideOverheadDimensions}
					/>
				</div>

				<BlueprintPageFooter pageNumber={page.pageNumber} />
			</BlueprintPageWrapper>

			{/* Sibling of the page box (not a descendant) so it isn't clipped by the page's own overflow:hidden -- same positioning context BlueprintPageFontControl's button cluster uses. */}
			{isOverheadLayout &&
				<BlueprintOverheadDimensionsToggle hidden={hideOverheadDimensions} onToggle={toggleHideOverheadDimensions} />
			}
		</>
	);
};
